import React, {
  useRef,
  useEffect,
  useCallback,
  useState,
  useMemo,
} from "react";
import { IconButton } from "@mui/material";
import { InlineIcon } from "@iconify/react";

interface SendMessageInputProps {
  onSend: (message: string) => void;
  maxCaracter: number;
  textareaRef?: React.RefObject<HTMLTextAreaElement>;
  disabled?: boolean;
  // Callback pour marquer les messages comme lus au focus
  onFocusMarkAsRead?: () => void;
}

const isMobile =
  typeof window !== "undefined" &&
  /Mobi|Android|iPhone/i.test(navigator.userAgent);

// Composant textarea simplifié sans mémoisation pour éviter la latence
const TextArea = ({
  value,
  onChange,
  maxCaracter,
  textareaRef,
  disabled,
  onFocus,
  onBlur,
  onKeyDown,
  onTouchStart,
  onTouchMove,
  onTouchEnd,
  onWheel,
}: {
  value: string;
  onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  maxCaracter: number;
  textareaRef?: React.RefObject<HTMLTextAreaElement>;
  disabled?: boolean;
  onFocus: () => void;
  onBlur: () => void;
  onKeyDown: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void;
  onTouchStart: (e: React.TouchEvent<HTMLTextAreaElement>) => void;
  onTouchMove: (e: React.TouchEvent<HTMLTextAreaElement>) => void;
  onTouchEnd: () => void;
  onWheel: (e: React.WheelEvent<HTMLTextAreaElement>) => void;
}) => (
  <textarea
    ref={textareaRef}
    className="send-message-input"
    style={{
      width: "100%",
      borderRadius: "20px",
      padding: "8px 12px 8px 40px",
      resize: "none",
      minHeight: "56px", // Hauteur pour 2 lignes (28px par ligne)
      maxHeight: "140px", // Environ 5 lignes (28px par ligne)
      border: "1px solid #e0e0e0",
      background: "#ffffff",
      boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
      lineHeight: "1.4",
      fontSize: "14px",
      fontFamily: "inherit",
      overflowY: "hidden",
      height: "56px", // Hauteur fixe initiale pour 2 lignes
      // Optimisations pour le scroll mobile
      WebkitOverflowScrolling: "touch", // Scroll fluide sur iOS
      touchAction: "manipulation", // Permet le scroll tactile
      overscrollBehavior: "contain", // Évite le scroll de la page parent
    }}
    maxLength={maxCaracter}
    value={value}
    onChange={onChange}
    onFocus={onFocus}
    onBlur={onBlur}
    onKeyDown={onKeyDown}
    onTouchStart={onTouchStart}
    onTouchMove={onTouchMove}
    onTouchEnd={onTouchEnd}
    onWheel={onWheel}
    disabled={disabled}
    rows={2}
  />
);

TextArea.displayName = "TextArea";

const SendMessageInput: React.FC<SendMessageInputProps> = ({
  onSend,
  maxCaracter,
  textareaRef,
  disabled,
  onFocusMarkAsRead,
}) => {
  const [value, setValue] = useState("");
  const [isScrolling, setIsScrolling] = useState(false);
  const [isKeyboardVisible, setIsKeyboardVisible] = useState(false);

  // HandleChange optimisé pour éviter les re-renders inutiles
  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLTextAreaElement>) => {
      const newValue = event.target.value;
      // Ne mettre à jour que si la valeur a vraiment changé
      setValue((prevValue) => (prevValue !== newValue ? newValue : prevValue));
    },
    []
  );

  const handleSend = useCallback(() => {
    const trimmedValue = value.trim();
    if (trimmedValue) {
      onSend(trimmedValue);
      setValue("");
    }
  }, [value, onSend]);

  // Handlers optimisés avec useCallback pour éviter les re-renders
  const handleFocus = useCallback(() => {
    // Marquer les messages comme lus au focus
    onFocusMarkAsRead?.();

    if (isMobile) {
      setIsKeyboardVisible(true);

      if (textareaRef?.current) {
        // Attendre que le clavier soit complètement affiché
        setTimeout(() => {
          if (textareaRef?.current) {
            textareaRef.current.scrollIntoView({
              behavior: "smooth",
              block: "nearest",
            });
          }
        }, 300); // Délai plus long pour le clavier
      }
    }
  }, [textareaRef, onFocusMarkAsRead]);

  const handleBlur = useCallback(() => {
    if (isMobile) {
      // Délai pour détecter si le clavier se ferme vraiment
      setTimeout(() => {
        setIsKeyboardVisible(false);
      }, 150);
    }
  }, []);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === " " && isMobile && textareaRef?.current) {
        // Debounce pour éviter les appels multiples
        setTimeout(() => {
          if (textareaRef?.current) {
            textareaRef.current.scrollIntoView({
              behavior: "smooth",
              block: "nearest",
            });
          }
        }, 0);
      }
    },
    [textareaRef]
  );

  // Gestionnaires d'événements tactiles pour empêcher la propagation du scroll
  const handleTouchStart = useCallback(
    (e: React.TouchEvent<HTMLTextAreaElement>) => {
      const textarea = e.currentTarget;
      const hasScroll = textarea.scrollHeight > textarea.clientHeight;

      if (hasScroll && isMobile) {
        // Marquer qu'on commence potentiellement un scroll
        setIsScrolling(true);

        // Gestion spéciale quand le clavier est visible
        if (isKeyboardVisible) {
          // Empêcher TOUS les événements de propagation quand le clavier est affiché
          e.preventDefault();
          e.stopPropagation();
          (e.nativeEvent as any).stopImmediatePropagation?.();

          // Sauvegarder la position initiale du touch
          const touch = e.touches[0];
          if (touch) {
            textarea.dataset.lastTouchY = touch.clientY.toString();
          }
        } else {
          // Comportement normal
          e.stopPropagation();
        }
      }
    },
    [isKeyboardVisible]
  );

  const handleTouchMove = useCallback(
    (e: React.TouchEvent<HTMLTextAreaElement>) => {
      const textarea = e.currentTarget;
      const hasScroll = textarea.scrollHeight > textarea.clientHeight;

      if (hasScroll && isScrolling && isMobile) {
        if (isKeyboardVisible) {
          // Gestion agressive quand le clavier est visible
          e.preventDefault();
          e.stopPropagation();
          (e.nativeEvent as any).stopImmediatePropagation?.();

          // Forcer le scroll manuel du textarea
          const touch = e.touches[0];
          if (touch && textarea.dataset.lastTouchY) {
            const deltaY =
              parseFloat(textarea.dataset.lastTouchY) - touch.clientY;
            textarea.scrollTop += deltaY * 0.5; // Facteur de sensibilité
            textarea.dataset.lastTouchY = touch.clientY.toString();
          }
        } else {
          // Empêcher la propagation du scroll vers le conteneur parent
          e.stopPropagation();
        }
      }
    },
    [isScrolling, isKeyboardVisible]
  );

  const handleTouchEnd = useCallback(() => {
    if (isMobile) {
      // Réinitialiser l'état de scroll
      setIsScrolling(false);

      // Nettoyer les données de touch
      if (textareaRef?.current) {
        delete textareaRef.current.dataset.lastTouchY;
      }
    }
  }, [textareaRef]);

  // Gestionnaire pour les événements de scroll avec la molette (desktop)
  const handleWheel = useCallback(
    (e: React.WheelEvent<HTMLTextAreaElement>) => {
      const textarea = e.currentTarget;
      const hasScroll = textarea.scrollHeight > textarea.clientHeight;

      if (hasScroll) {
        // Empêcher la propagation du scroll vers le parent
        e.stopPropagation();
      }
    },
    []
  );

  // Optimisation du resize pour éviter les re-renders en boucle et le clignotement
  useEffect(() => {
    if (!textareaRef?.current) return;

    const textareaElt = textareaRef.current;

    // Utilisation de requestAnimationFrame pour éviter les re-renders en cascade
    const rafId = requestAnimationFrame(() => {
      if (!textareaElt || !textareaElt.isConnected) return;

      try {
        // Constantes pour les hauteurs
        const minHeight = 56; // 2 lignes
        const maxHeight = 140; // 5 lignes

        // Sauvegarder la hauteur actuelle pour éviter le clignotement
        const currentHeight = parseInt(textareaElt.style.height) || minHeight;

        // Créer un élément temporaire invisible pour mesurer sans affecter l'affichage
        const tempTextarea = textareaElt.cloneNode(true) as HTMLTextAreaElement;
        tempTextarea.style.position = "absolute";
        tempTextarea.style.visibility = "hidden";
        tempTextarea.style.height = "auto";
        tempTextarea.style.minHeight = "0";
        tempTextarea.style.maxHeight = "none";
        tempTextarea.value = value;

        // Ajouter temporairement au DOM pour mesurer
        document.body.appendChild(tempTextarea);
        const contentHeight = tempTextarea.scrollHeight;
        document.body.removeChild(tempTextarea);

        // Calculer la nouvelle hauteur en respectant les limites
        let newHeight = Math.max(minHeight, contentHeight);
        newHeight = Math.min(newHeight, maxHeight);

        // Déterminer si on a besoin du scroll
        const needsScroll = contentHeight > maxHeight;
        const newOverflow = needsScroll ? "auto" : "hidden";

        // Appliquer les changements seulement si nécessaire pour éviter le clignotement
        if (currentHeight !== newHeight) {
          textareaElt.style.height = `${newHeight}px`;
        }

        if (textareaElt.style.overflowY !== newOverflow) {
          textareaElt.style.overflowY = newOverflow;

          // Optimisations spécifiques pour mobile quand le scroll est activé
          if (needsScroll && isMobile) {
            // S'assurer que le scroll tactile fonctionne sur mobile
            (textareaElt.style as any).WebkitOverflowScrolling = "touch";
            textareaElt.style.touchAction = "pan-y"; // Permet uniquement le scroll vertical

            // Forcer le focus pour activer le scroll sur certains navigateurs mobiles
            setTimeout(() => {
              if (textareaElt && document.activeElement === textareaElt) {
                textareaElt.blur();
                textareaElt.focus();
              }
            }, 50);
          }
        }

        // Si on a atteint la hauteur max et qu'il y a du nouveau contenu,
        // scroller vers le bas pour voir le texte tapé
        if (needsScroll && value) {
          textareaElt.scrollTop = textareaElt.scrollHeight;
        }
      } catch (error) {
        console.warn("Erreur lors du redimensionnement du textarea:", error);
        // En cas d'erreur, restaurer la hauteur minimale
        if (textareaElt) {
          textareaElt.style.height = "56px";
          textareaElt.style.overflowY = "hidden";
        }
      }
    });

    return () => cancelAnimationFrame(rafId);
  }, [value, textareaRef]);

  // Mémorisation des styles pour éviter les re-renders
  const containerStyle = useMemo(
    () => ({
      display: "flex",
      alignItems: "center",
      gap: "8px",
      width: "100%",
    }),
    []
  );

  const innerContainerStyle = useMemo(
    () => ({
      position: "relative" as const,
      flex: 1,
      display: "flex",
      alignItems: "center",
    }),
    []
  );

  const counterStyle = useMemo(
    () => ({
      position: "absolute" as const,
      bottom: "-20px",
      left: "0",
    }),
    []
  );

  return (
    <div className="sender-input" style={containerStyle}>
      <div style={innerContainerStyle}>
        <TextArea
          value={value}
          onChange={handleChange}
          maxCaracter={maxCaracter}
          textareaRef={textareaRef}
          disabled={disabled}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          onWheel={handleWheel}
        />
      </div>
      <IconButton
        className={`send-btn ${value !== "" ? "active" : ""}`}
        onClick={handleSend}
        disabled={disabled}
      >
        <InlineIcon icon="fluent:send-28-regular" />
      </IconButton>
      <small style={counterStyle}>
        {value.length} / {maxCaracter} caractères
      </small>
    </div>
  );
};

export default SendMessageInput;
